import { makeStyles } from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from '@backstage/core-components';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Render markdown with the same typographic system the rest of the
   * Bosch portal uses. We deliberately scope styles via `.markdown` so
   * heading / list rules can't leak into surrounding InfoCards. Sizes
   * here mirror `KindsGuidePage` / `WelcomeBanner` for visual rhythm.
   */
  markdown: {
    color: boschGray[15],
    fontSize: '0.95rem',
    lineHeight: 1.6,

    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: boschGray[15],
      fontWeight: 600,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1),
      lineHeight: 1.25,
    },
    '& h1': { fontSize: '1.6rem' },
    '& h2': { fontSize: '1.35rem' },
    '& h3': { fontSize: '1.15rem' },
    '& h4': { fontSize: '1rem' },

    '& p': {
      margin: theme.spacing(0, 0, 1.5),
    },

    '& a': {
      color: boschBlue[50],
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
        color: boschBlue[40],
      },
    },

    '& ul, & ol': {
      margin: theme.spacing(0, 0, 1.5),
      paddingLeft: theme.spacing(3),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },

    '& blockquote': {
      borderLeft: `4px solid ${boschBlue[80]}`,
      backgroundColor: boschBlue[95],
      padding: theme.spacing(1, 1.5),
      margin: theme.spacing(0, 0, 2),
      color: boschGray[25],
      borderRadius: 4,
      '& p:last-child': { marginBottom: 0 },
    },

    '& code': {
      fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
      fontSize: '0.85em',
      backgroundColor: boschGray[95],
      border: `1px solid ${boschGray[90]}`,
      borderRadius: 3,
      padding: '0 4px',
    },
    '& pre': {
      backgroundColor: boschGray[95],
      border: `1px solid ${boschGray[90]}`,
      borderLeft: `3px solid ${boschBlue[50]}`,
      borderRadius: 4,
      padding: theme.spacing(1.5, 2),
      overflowX: 'auto',
      marginBottom: theme.spacing(2),
      '& code': {
        background: 'transparent',
        border: 'none',
        padding: 0,
        fontSize: '0.8rem',
      },
    },

    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: theme.spacing(2),
      fontSize: '0.85rem',
    },
    '& th, & td': {
      border: `1px solid ${boschGray[90]}`,
      padding: theme.spacing(0.75, 1.25),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: boschGray[95],
      fontWeight: 700,
      color: boschGray[20],
    },

    '& hr': {
      border: 'none',
      borderTop: `1px solid ${boschGray[90]}`,
      margin: theme.spacing(2, 0),
    },

    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: 4,
    },
  },
}));

export type MarkdownBlockProps = {
  body: string;
};

/**
 * Renders GitHub-flavoured Markdown (paragraphs, headings, lists,
 * tables, fenced code, blockquotes, autolinks) inside a Bosch-styled
 * container.
 *
 * Used as the `markdown` widget in the content-page widget registry
 * (`packages/app/src/modules/contentPages/widgets.tsx`). The admin
 * writes prose in `/create > New portal page`'s "Markdown"
 * widget step; the resulting `body` string is rendered here.
 *
 * Why this exists as its own component (instead of ad-hoc
 * `<ReactMarkdown />` inline in the page renderer):
 *
 *   - Lets us pin the markdown styling in one place — every content
 *     page renders the SAME h1/h2/code/table treatment, regardless of
 *     where it's used (could later show up inside an InfoCard, a tab
 *     panel, etc.).
 *   - Centralises the `react-router-dom`-aware link override. Plain
 *     `<a href>` would cause full-page navigations and bounce the
 *     user back to `/sign-in` for internal links — same trap
 *     `WelcomeCard` documents at length.
 */
export const MarkdownBlock = ({ body }: MarkdownBlockProps) => {
  const classes = useStyles();
  return (
    <div className={classes.markdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          /*
           * Replace plain `<a>` with our routing-aware `Link` for
           * in-app destinations and a target="_blank" anchor for
           * external ones.
           *
           * We deliberately drop the `...rest` props instead of
           * forwarding them: react-markdown types the anchor props as
           * the full HTML `<a>` spec (which includes a `color`
           * attribute typed as `string`), while
           * `Link` from `@backstage/core-components` types `color` as
           * a restricted enum ("primary" | "secondary" | …). Spreading
           * would cause a TS error. The only meaningful prop we'd want
           * to keep is `title` (tooltip), but no markdown syntax sets
           * it, so dropping `...rest` is safe in practice.
           */
          a: ({ href, children, title }) => {
            const isExternal =
              !!href && /^(https?:)?\/\//i.test(href);
            if (isExternal) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={title}
                >
                  {children}
                </a>
              );
            }
            return (
              <Link to={href ?? '#'} title={title}>
                {children}
              </Link>
            );
          },
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
};
